/**
 * WORLD clue + red-herring props — InteractTarget-compatible meshes.
 *
 * Ids are the binding convention for GAME's ClueDefs (no event exists for
 * publishing them; see deviations). Six clues, three red herrings — herrings
 * are also kind 'clue' so GAME may bind them to worthless ClueDefs.
 *
 * Consumes `clue:collected` to remove the collected prop from the scene.
 */
import { BoxGeometry, CylinderGeometry, Group, Mesh, MeshStandardMaterial } from 'three'
import type { EventBus, Vec3 } from '../core/types'
import { interactRadius, tagInteract } from './helpers'

type ClueShape = 'box' | 'cylinder'

interface CluePlacement {
  readonly id: string
  readonly prompt: string
  readonly position: Vec3
  readonly size: Vec3
  readonly shape: ClueShape
  readonly color: number
  readonly emissive: number
}

const CLUE_GLOW_INTENSITY = 0.6
const SIDEWALK_TOP_Y = 0.15

/**
 * Six real clues. Ids match GAME's ClueDefs in src/game/data.ts exactly
 * (integrator-aligned): matchbook, torn-photograph, monogrammed-cufflink,
 * pawn-ticket (outdoor); ledger-page, threatening-note (indoor).
 */
const CLUE_PLACEMENTS: readonly CluePlacement[] = [
  {
    id: 'matchbook',
    prompt: 'Examine matchbook',
    position: { x: 7.4, y: SIDEWALK_TOP_Y + 0.015, z: -10.6 },
    size: { x: 0.12, y: 0.03, z: 0.09 },
    shape: 'box',
    color: 0x8a2c2c,
    emissive: 0x551111
  },
  {
    id: 'torn-photograph',
    prompt: 'Examine torn photograph',
    position: { x: -7.6, y: SIDEWALK_TOP_Y + 0.012, z: 14 },
    size: { x: 0.22, y: 0.012, z: 0.16 },
    shape: 'box',
    color: 0xb8b09a,
    emissive: 0x404038
  },
  {
    id: 'monogrammed-cufflink',
    prompt: 'Examine monogrammed cufflink',
    position: { x: 0.9, y: 0.02, z: -33.6 },
    size: { x: 0.06, y: 0.025, z: 0.06 },
    shape: 'cylinder',
    color: 0xc9a227,
    emissive: 0x6a4f10
  },
  {
    id: 'pawn-ticket',
    prompt: 'Examine pawn ticket',
    position: { x: 20, y: 0.03, z: 1.4 },
    size: { x: 0.16, y: 0.012, z: 0.08 },
    shape: 'box',
    color: 0xcfc8ae,
    emissive: 0x44402e
  },
  {
    id: 'ledger-page',
    prompt: 'Examine ledger page',
    position: { x: 37.5, y: 0.85, z: -0.95 },
    size: { x: 0.3, y: 0.05, z: 0.4 },
    shape: 'box',
    color: 0x223322,
    emissive: 0x112211
  },
  {
    id: 'threatening-note',
    prompt: 'Examine threatening note',
    position: { x: 39.5, y: 1.33, z: 4.4 },
    size: { x: 0.2, y: 0.012, z: 0.14 },
    shape: 'box',
    color: 0xd8d4c8,
    emissive: 0x444036
  }
]

/** Three red herrings — ids also match GAME's worthless ClueDefs. */
const HERRING_PLACEMENTS: readonly CluePlacement[] = [
  {
    id: 'lipstick-cigarette',
    prompt: 'Examine lipstick-stained cigarette',
    position: { x: 12.5, y: 0.02, z: 1.8 },
    size: { x: 0.16, y: 0.025, z: 0.03 },
    shape: 'cylinder',
    color: 0xcfc5b0,
    emissive: 0x5a3020
  },
  {
    id: 'racing-form',
    prompt: 'Examine discarded racing form',
    position: { x: -8.2, y: SIDEWALK_TOP_Y + 0.012, z: -4 },
    size: { x: 0.28, y: 0.012, z: 0.2 },
    shape: 'box',
    color: 0x9a9484,
    emissive: 0x33312a
  },
  {
    id: 'love-letter',
    prompt: 'Examine unsent love letter',
    position: { x: 31, y: 0.03, z: -4.2 },
    size: { x: 0.18, y: 0.012, z: 0.13 },
    shape: 'box',
    color: 0xe3ddc8,
    emissive: 0x4a4536
  }
]

const createClueMesh = (placement: CluePlacement): Mesh => {
  const geometry =
    placement.shape === 'cylinder'
      ? new CylinderGeometry(placement.size.x / 2, placement.size.x / 2, placement.size.y, 10)
      : new BoxGeometry(placement.size.x, placement.size.y, placement.size.z)
  const material = new MeshStandardMaterial({
    color: placement.color,
    emissive: placement.emissive,
    emissiveIntensity: CLUE_GLOW_INTENSITY,
    roughness: 0.5,
    metalness: 0.15
  })
  const mesh = new Mesh(geometry, material)
  mesh.position.set(placement.position.x, placement.position.y, placement.position.z)
  mesh.castShadow = true
  tagInteract(mesh, {
    id: placement.id,
    kind: 'clue',
    prompt: placement.prompt,
    radius: interactRadius(),
    clueId: placement.id
  })
  return mesh
}

export interface CluesBuild {
  readonly group: Group
  /** Slow emissive pulse so interactables glint instead of pixel-hunting. */
  update(dt: number): void
}

/** ~0.8 Hz glint: emissiveIntensity oscillates between the min/max factors —
 * luminance priority on the interactable, visible even on lit pavement. */
const PULSE_HZ = 0.8
const PULSE_MIN_FACTOR = 1.2
const PULSE_MAX_FACTOR = 5.0
const TWO_PI = Math.PI * 2

export const buildClues = (bus: EventBus): CluesBuild => {
  const group = new Group()
  group.name = 'clue-props'
  const all = [...CLUE_PLACEMENTS, ...HERRING_PLACEMENTS]
  const meshById = new Map<string, Mesh>(all.map((p) => [p.id, createClueMesh(p)]))
  meshById.forEach((mesh) => group.add(mesh))

  bus.on('clue:collected', ({ clue }) => {
    const mesh = meshById.get(clue.id)
    if (!mesh) return
    mesh.removeFromParent()
    delete mesh.userData['interactId']
    meshById.delete(clue.id)
  })

  let time = 0
  const update = (dt: number): void => {
    time += dt
    const wave = 0.5 * (1 + Math.sin(time * PULSE_HZ * TWO_PI))
    const factor = PULSE_MIN_FACTOR + (PULSE_MAX_FACTOR - PULSE_MIN_FACTOR) * wave
    meshById.forEach((mesh) => {
      const material = mesh.material as MeshStandardMaterial
      material.emissiveIntensity = CLUE_GLOW_INTENSITY * factor
    })
  }

  return { group, update }
}
